import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { catLabel, CATEGORIES, t } from "@/lib/i18n";
import WordCard from "@/components/WordCard";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function HomePage() {
  const { lang, sourceLang } = useApp();
  const [words, setWords] = useState([]);
  const [wotd, setWotd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [intensity, setIntensity] = useState([0, 5]);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const PAGE_SIZE = 24;

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.all([
      api.get(`/words?source_lang=${sourceLang}`).then((r) => {
        if (!ignore) setWords(r.data);
      }),
      api
        .get(`/word-of-the-day?source_lang=${sourceLang}`)
        .then((r) => {
          if (!ignore) setWotd(r.data);
        })
        .catch(() => {
          if (!ignore) setWotd(null);
        }),
    ]).finally(() => {
      if (!ignore) setLoading(false);
    });
    return () => {
      ignore = true;
    };
  }, [sourceLang]);

  useEffect(() => {
    setPage(1);
  }, [q, category, intensity]);

  const filtered = useMemo(() => {
    return words.filter((w) => {
      if (category !== "all" && w.category !== category) return false;
      const i = w.intensity_avg || 0;
      if (i < intensity[0] || i > intensity[1]) return false;
      if (q) {
        const hay = [
          w.word,
          w.definition,
          w.translations?.es,
          w.translations?.en,
          w.translations?.pl,
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [words, q, category, intensity]);

  return (
    <main data-testid="home-page" className="max-w-6xl mx-auto px-6 lg:px-8">
      <Seo
        title={
          sourceLang === "es"
            ? "I Swear · insultos españoles (ES → NO · EN · PL)"
            : "I Swear · norsk banneordbok (NO → ES · EN · PL)"
        }
        description={
          sourceLang === "es"
            ? t("brand_tagline_es", lang)
            : t("brand_tagline", lang)
        }
        image="https://static.prod-images.emergentagent.com/jobs/fa533497-bc67-4bda-9cd5-b6af234a94db/images/22cf6f734adf5a44713dff7bcf8f75c6a2760a7d1785ef32c72211254491f2d5.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: sourceLang === "es" ? "I Swear — insultos españoles" : "I Swear — norsk banneordbok",
          url: sourceLang === "es" ? "https://iswear.no/es" : "https://iswear.no/",
          description: sourceLang === "es" ? t("brand_tagline_es", lang) : t("brand_tagline", lang),
          inLanguage: ["no", "es", "en", "pl"],
        }}
      />
      {/* Hero */}
      <section className="relative pt-16 sm:pt-24 pb-12 sm:pb-20">
        <span className="text-[11px] tracking-eyebrow text-[#E85D04] font-medium">
          {sourceLang === "es" ? "Español → NO · EN · PL" : "Norsk → ES · EN · PL"}
        </span>
        <h1 className="mt-4 font-serif-display font-bold tracking-tighter leading-[0.95] text-gray-900 text-5xl sm:text-7xl lg:text-8xl">
          {sourceLang === "es" ? (
            <>
              Insulta como
              <br />
              <em className="italic text-[#E85D04]">un español.</em>
            </>
          ) : (
            <>
              Swear like
              <br />
              <em className="italic text-[#E85D04]">a Norwegian.</em>
            </>
          )}
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed">
          {t(sourceLang === "es" ? "brand_tagline_es" : "brand_tagline", lang)}.{" "}
          <span className="text-gray-500">
            {words.length} {t("total_words", lang)}.
          </span>
        </p>
        {stats && (
          <div
            data-testid="collection-counter"
            className="mt-5 inline-flex items-center gap-2 text-sm font-mono-code text-gray-500"
          >
            <span className={sourceLang === "no" ? "text-gray-900 font-medium" : ""}>
              {stats.no} norske
            </span>
            <span className="text-gray-300">·</span>
            <span className={sourceLang === "es" ? "text-gray-900 font-medium" : ""}>
              {stats.es} españoles
            </span>
          </div>
        )}
      </section>

      {/* Word of the day */}
      {wotd && (
        <section data-testid="wotd-section" className="mb-12">
          <Link
            to={`/word/${wotd.id}`}
            className="block bg-white border border-gray-200 rounded-2xl p-8 sm:p-10 hover:border-[#E85D04] transition-colors group"
          >
            <span className="text-[11px] tracking-eyebrow font-medium text-[#E85D04]">
              {t("word_of_day", lang)}
            </span>
            <h2 className="mt-3 font-serif-display font-bold tracking-tighter text-gray-900 text-5xl sm:text-7xl leading-none group-hover:text-[#E85D04] transition-colors">
              {wotd.word}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl">
              {(wotd.definition?.[lang] || wotd.definition?.es || "")}
            </p>
          </Link>
        </section>
      )}

      {/* Filters */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-10 pb-8 border-b border-gray-200">
        <div className="md:col-span-6 relative">
          <MagnifyingGlass
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            data-testid="search-input"
            placeholder={t("search_placeholder", lang)}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 py-6 rounded-full bg-white border-gray-200 focus-visible:ring-[#E85D04]"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="category-filter"
              className="py-6 rounded-full bg-white border-gray-200"
            >
              <SelectValue placeholder={t("filter_category", lang)} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("all", lang)}</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {catLabel(c, lang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 bg-white border border-gray-200 rounded-full px-5 py-3 flex items-center gap-3">
          <span className="text-[10px] tracking-eyebrow text-gray-500 whitespace-nowrap">
            {t("filter_intensity", lang)}
          </span>
          <Slider
            data-testid="intensity-filter"
            value={intensity}
            onValueChange={setIntensity}
            min={0}
            max={5}
            step={0.5}
            className="flex-1"
          />
          <span className="font-mono-code text-xs text-gray-700">
            {intensity[0]}-{intensity[1]}
          </span>
        </div>
      </section>

      {/* Results */}
      {loading ? (
        <div data-testid="loading-state" className="py-20 text-center text-gray-500">
          {t("loading", lang)}
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="empty-state" className="py-20 text-center text-gray-500">
          {t("no_results", lang)}
        </div>
      ) : (
        <>
          <section
            data-testid="words-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 pb-10"
          >
            {filtered.slice(0, page * PAGE_SIZE).map((w, idx) => (
              <WordCard key={w.id} word={w} idx={idx % PAGE_SIZE} />
            ))}
          </section>
          {filtered.length > page * PAGE_SIZE && (
            <div className="flex justify-center pb-24">
              <button
                data-testid="load-more-btn"
                onClick={() => setPage(page + 1)}
                className="rounded-full border border-gray-300 hover:border-gray-900 px-8 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t("load_more", lang)} ({filtered.length - page * PAGE_SIZE})
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
